/*
 * Runtime tenant and campus localisation.
 *
 * Responsibilities:
 * - Resolve the university tenant from the hostname.
 * - Restore a valid campus selection from the URL or localStorage.
 * - Merge university tokens with campus-specific tokens.
 * - Inject the resulting runtime configuration as this.$tenant.
 * - Synchronise the profile Vuex tenant/campus state on the client.
 *
 * Host examples:
 * - atu.myuse.ie  -> tenant "atu"
 * - setu.myuse.ie -> tenant "setu"
 * - ul.myuse.ie   -> tenant "ul"
 *
 * Campus is not another hostname segment. It is stored separately:
 * - tenant: atu
 * - campus: sligo
 */

import {
  TENANTS,
  DEFAULT_TENANT_KEY,
  resolveTenantKeyFromHost,
  getTenantConfig,
} from '~/tenants/index.js'

const normaliseCampusKey = (
  value
) => {
  if (
    value == null ||
    value === ''
  ) {
    return null
  }

  return String(value)
    .trim()
    .toLowerCase()
}

const cloneObject = (
  value
) => {
  if (
    !value ||
    typeof value !== 'object'
  ) {
    return value
  }

  try {
    return JSON.parse(
      JSON.stringify(value)
    )
  } catch (_) {
    return {
      ...value,
    }
  }
}

const getRequestHost = (
  ctx
) => {
  if (
    process.server
  ) {
    return (
      ctx.req &&
      ctx.req.headers &&
      ctx.req.headers.host
        ? ctx.req.headers.host
        : ''
    )
  }

  if (
    typeof window !==
      'undefined'
  ) {
    return (
      window.location.host ||
      window.location.hostname ||
      ''
    )
  }

  return ''
}

const getCampusFromQuery = () => {
  if (
    !process.client ||
    typeof window ===
      'undefined'
  ) {
    return null
  }

  try {
    const params =
      new URLSearchParams(
        window.location.search ||
        ''
      )

    return normaliseCampusKey(
      params.get('campus')
    )
  } catch (_) {
    return null
  }
}

const getCampusFromStorage = () => {
  if (
    !process.client ||
    typeof window ===
      'undefined'
  ) {
    return null
  }

  try {
    return normaliseCampusKey(
      window.localStorage.getItem(
        'campus'
      )
    )
  } catch (_) {
    return null
  }
}

const isValidCampus = (
  tenant,
  campusKey
) => {
  if (
    !tenant ||
    !campusKey ||
    !tenant.campuses ||
    typeof tenant.campuses !==
      'object'
  ) {
    return false
  }

  return Object.prototype
    .hasOwnProperty.call(
      tenant.campuses,
      campusKey
    )
}

const clearInvalidCampus = () => {
  if (
    !process.client ||
    typeof window ===
      'undefined'
  ) {
    return
  }

  try {
    window.localStorage.removeItem(
      'campus'
    )
  } catch (_) {}
}

export default function (
  ctx,
  inject
) {
  const {
    store,
  } = ctx

  const host =
    getRequestHost(ctx)

  const resolvedTenantKey =
    resolveTenantKeyFromHost(
      host
    )

  const tenantKey =
    TENANTS[
      resolvedTenantKey
    ]
      ? resolvedTenantKey
      : DEFAULT_TENANT_KEY

  const baseTenant =
    TENANTS[tenantKey] ||
    TENANTS[
      DEFAULT_TENANT_KEY
    ]

  /*
   * On the server, localStorage does not exist, so only the
   * university-level configuration can be resolved.
   *
   * On the client, an explicit ?campus= value takes priority
   * over a previously saved localStorage selection.
   */
  const requestedCampus =
    process.client
      ? (
          getCampusFromQuery() ||
          getCampusFromStorage()
        )
      : null

  const campusKey =
    isValidCampus(
      baseTenant,
      requestedCampus
    )
      ? requestedCampus
      : null

  /*
   * Remove a stale campus when:
   * - it does not belong to this tenant; or
   * - the current tenant has no campus picker.
   *
   * This prevents a previous ATU selection from leaking into SETU,
   * UCC, UL or another university in the same browser.
   */
  if (
    process.client &&
    requestedCampus &&
    !campusKey
  ) {
    clearInvalidCampus()
  }

  /*
   * getTenantConfig returns university base tokens merged with the
   * selected campus tokens.
   */
  const resolvedConfig =
    getTenantConfig(
      tenantKey,
      campusKey
    )

  /*
   * Never inject the original TENANTS entry directly.
   *
   * Components such as the campus picker update this.$tenant.tokens
   * at runtime. A cloned object prevents those changes from mutating
   * the shared TENANTS catalogue.
   */
  const runtimeTenant = {
    ...cloneObject(
      baseTenant
    ),

    ...cloneObject(
      resolvedConfig || {}
    ),

    key: tenantKey,

    tokens: {
      ...(
        resolvedConfig &&
        resolvedConfig.tokens
          ? cloneObject(
              resolvedConfig.tokens
            )
          : cloneObject(
              baseTenant.tokens ||
              {}
            )
      ),
    },

    campuses:
      cloneObject(
        baseTenant.campuses ||
        {}
      ),

    campus_key:
      campusKey,

    campus_label:
      resolvedConfig &&
      resolvedConfig
        .selectedCampus &&
      resolvedConfig
        .selectedCampus.label
        ? resolvedConfig
            .selectedCampus.label
        : null,

    selectedCampus:
      resolvedConfig &&
      resolvedConfig
        .selectedCampus
        ? cloneObject(
            resolvedConfig
              .selectedCampus
          )
        : null,
  }

  inject(
    'tenant',
    runtimeTenant
  )

  /*
   * Keep the existing profile localisation state aligned with
   * the injected runtime tenant.
   *
   * This is client-only because profile state created during SSR
   * must not write browser storage or assume a campus selection.
   */
  if (
    process.client &&
    store
  ) {
    try {
      store.commit(
        'profile/setTenantSelection',
        {
          tenant: tenantKey,
          campus: campusKey,
        }
      )
    } catch (error) {
      console.error(
        'Could not synchronise tenant and campus state:',
        error
      )
    }
  }
}